import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  Autocomplete,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Cancel,
  Search,
  Phone,
  Email,
  LocationOn,
  Person,
  Business,
  Visibility,
  Assignment,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Payment,
  Schedule,
  CheckCircle,
  Pending,
  ExpandMore,
  AttachMoney,
  CalendarToday,
  Description,
  Diamond,
  EmojiEvents,
  AccountBalance,
  Receipt,
  Create
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as goldsmithActions from '../redux/actions/goldsmithActions';
import { getCustomers } from '../redux/actions/customerActions';
import { convertOrderToInvoice } from '../redux/actions/invoiceActions';

const CustomerOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    customerOrders, 
    ordersLoading, 
    ordersError,
    goldsmiths 
  } = useSelector(state => state.goldsmith);
  const { customers } = useSelector(state => state.customers);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    customer_id: null,
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    product_description: '',
    design_details: '',
    estimated_weight: '',
    estimated_touch: '',
    estimated_amount: '',
    advance_amount: '',
    advance_payment_mode: 'Cash',
    advance_reference: '',
    notes: ''
  });

  useEffect(() => {
    dispatch(goldsmithActions.getCustomerOrders());
    dispatch(goldsmithActions.getGoldsmiths());
    dispatch(getCustomers({ page: 1, limit: 1000 }));
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    dispatch(goldsmithActions.getCustomerOrders({ search: e.target.value }));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    const params = { search: searchTerm };
    if (e.target.value !== 'all') {
      params.status = e.target.value;
    }
    dispatch(goldsmithActions.getCustomerOrders(params));
  };

  const handleOpenDialog = (mode, order = null) => {
    setDialogMode(mode);
    if (mode === 'edit' || mode === 'view') {
      setFormData({
        customer_id: order.customer || null,
        order_date: order.order_date || '',
        expected_delivery_date: order.expected_delivery_date || '',
        product_description: order.product_description || '',
        design_details: order.design_details || '',
        estimated_weight: order.estimated_weight || '',
        estimated_touch: order.estimated_touch || '',
        estimated_amount: order.estimated_amount || '',
        advance_amount: order.advance_amount || '',
        advance_payment_mode: 'Cash',
        advance_reference: '',
        notes: order.notes || ''
      });
      setSelectedOrder(order);
    } else {
      setFormData({
        customer_id: null,
        order_date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        product_description: '',
        design_details: '',
        estimated_weight: '',
        estimated_touch: '',
        estimated_amount: '',
        advance_amount: '',
        advance_payment_mode: 'Cash',
        advance_reference: '',
        notes: ''
      });
      setSelectedOrder(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
    setCurrentTab(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      customer_id: newValue?.id || null
    }));
  };

  const calculateEstimatedPure = () => {
    if (formData.estimated_weight && formData.estimated_touch) {
      return (parseFloat(formData.estimated_weight) * parseFloat(formData.estimated_touch)) / 100;
    }
    return 0;
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await dispatch(goldsmithActions.createCustomerOrder(formData));
        setSnackbar({ open: true, message: 'Customer order created successfully!', severity: 'success' });
      } else if (dialogMode === 'edit') {
        await dispatch(goldsmithActions.updateCustomerOrder(selectedOrder.id, formData));
        setSnackbar({ open: true, message: 'Order updated successfully!', severity: 'success' });
      }
      handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    }
  };

  const handleConvertToInvoice = async (order) => {
    try {
      const result = await dispatch(convertOrderToInvoice(order.id));
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: `Order converted to invoice successfully! Invoice #: ${result.data.invoice_number}`, 
          severity: 'success' 
        });
        // Refresh the orders list
        dispatch(goldsmithActions.getCustomerOrders());
        // Navigate to invoices page
        navigate('/invoices');
      } else {
        setSnackbar({ 
          open: true, 
          message: result.message || 'Failed to convert order to invoice', 
          severity: 'error' 
        });
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'An error occurred while converting order to invoice', 
        severity: 'error' 
      });
    }
  };

  const handleCancelOrder = (order) => {
    setSelectedOrder(order);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (selectedOrder) {
      try {
        // TODO: Implement cancel order API call
        console.log('Cancelling order:', selectedOrder.id);
        
        // For now, just show success message
        setSnackbar({ 
          open: true, 
          message: `Order #${selectedOrder.id} cancelled successfully!`, 
          severity: 'success' 
        });
        
        setCancelDialogOpen(false);
        setSelectedOrder(null);
        
        // Refresh the orders list
        dispatch(goldsmithActions.getCustomerOrders());
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: 'Failed to cancel order. Please try again.', 
          severity: 'error' 
        });
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      in_progress: 'primary',
      completed: 'success',
      delivered: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Pending />,
      confirmed: <CheckCircle />,
      in_progress: <TrendingUp />,
      completed: <CheckCircle />,
      delivered: <CheckCircle />,
      cancelled: <TrendingDown />
    };
    return icons[status] || <Pending />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const filteredOrders = customerOrders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.product_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            <Assignment sx={{ mr: 2, verticalAlign: 'middle' }} />
            Customer Orders
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage customer orders and track progress
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Create />}
          onClick={() => navigate('/new-customer-order')}
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
            }
          }}
        >
          Create New Order
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<Assignment />}
                  label={`${filteredOrders.length} Orders`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Order Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estimated Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Advance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Goldsmith</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordersLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {order.order_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(order.order_date)}
                        </Typography>
                        {order.expected_delivery_date && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Due: {formatDate(order.expected_delivery_date)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {order.customer?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customer?.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.product_description}
                        </Typography>
                        {order.estimated_weight && (
                          <Typography variant="caption" color="text.secondary">
                            Weight: {order.estimated_weight}g
                          </Typography>
                        )}
                        {order.estimated_touch && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Touch: {order.estimated_touch}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(order.estimated_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {formatCurrency(order.advance_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(order.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {order.goldsmith ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{order.goldsmith.name}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not Assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', order)}
                            color="info"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/customer-orders/edit/${order.id}`)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {(order.status === 'completed' || order.status === 'confirmed' || order.status === 'in_progress') && (
                          <Tooltip title="Convert to Invoice">
                            <IconButton
                              size="small"
                              onClick={() => handleConvertToInvoice(order)}
                              color="success"
                            >
                              <Receipt />
                            </IconButton>
                          </Tooltip>
                        )}
                        {order.status !== 'cancelled' && (
                          <Tooltip title="Cancel Order">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelOrder(order)}
                              color="error"
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Order Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        disableScrollLock={false}
        sx={{
          '& .MuiDialog-paper': {
            margin: 2,
            maxHeight: 'calc(100vh - 32px)',
            zIndex: 1300,
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1200px'
          },
          '& .MuiBackdrop-root': {
            zIndex: 1299
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
          color: 'white',
          p: 3
        }}>
          <Typography variant="h5" fontWeight="bold">
            {dialogMode === 'create' && 'Create New Order'}
            {dialogMode === 'edit' && 'Edit Order'}
            {dialogMode === 'view' && 'Order Details'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: 3,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          position: 'relative',
          zIndex: 1
        }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option.name}
                value={formData.customer_id ? customers.find(c => c.id === formData.customer_id) : null}
                onChange={handleCustomerChange}
                disabled={dialogMode === 'view'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.phone} {option.email && `• ${option.email}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Order Date"
                name="order_date"
                type="date"
                value={formData.order_date}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Expected Delivery"
                name="expected_delivery_date"
                type="date"
                value={formData.expected_delivery_date}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Schedule /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Product Description"
                name="product_description"
                value={formData.product_description}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
                multiline
                rows={2}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Description /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Design Details"
                name="design_details"
                value={formData.design_details}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                multiline
                rows={3}
                placeholder="Describe the design requirements, specifications, etc."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estimated Weight (g)"
                name="estimated_weight"
                type="number"
                value={formData.estimated_weight}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.001" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Diamond /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estimated Touch"
                name="estimated_touch"
                type="number"
                value={formData.estimated_touch}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01", min: 0, max: 100 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estimated Amount"
                name="estimated_amount"
                type="number"
                value={formData.estimated_amount}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>
            {formData.estimated_weight && formData.estimated_touch && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Estimated Pure:</strong> {calculateEstimatedPure().toFixed(3)}g
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Advance Amount"
                name="advance_amount"
                type="number"
                value={formData.advance_amount}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Payment /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={dialogMode === 'view'}>
                <InputLabel>Advance Payment Mode</InputLabel>
                <Select
                  name="advance_payment_mode"
                  value={formData.advance_payment_mode}
                  onChange={handleInputChange}
                  label="Advance Payment Mode"
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Card">Card</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                name="advance_reference"
                value={formData.advance_reference}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                placeholder="Transaction reference, cheque number, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                multiline
                rows={2}
                placeholder="Additional notes or special instructions"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        {dialogMode !== 'view' && (
          <DialogActions sx={{ 
            p: 3, 
            gap: 2,
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 2
          }}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 4,
                background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388e3c 0%, #689f38 100%)',
                }
              }}
            >
              {dialogMode === 'create' ? 'Create Order' : 'Update Order'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel order <strong>#{selectedOrder?.id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The order will be marked as cancelled and removed from active records.
          </Typography>
          {selectedOrder && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Order Details:</Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {selectedOrder.customer?.name || 'N/A'}<br/>
                <strong>Product:</strong> {selectedOrder.design_details || 'N/A'}<br/>
                <strong>Status:</strong> {selectedOrder.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Order</Button>
          <Button 
            onClick={handleCancelConfirm} 
            variant="contained"
            color="error"
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerOrders;
